import { useMsal } from "@azure/msal-react";
import { getUserRoles, hasRole } from "./auth";
import { useTechIdentity } from "./TechIdentityContext";

export function useRoles() {
  const { accounts } = useMsal();
  const { viewingAsTech, viewingAsIP } = useTechIdentity();

  const realRoles   = getUserRoles(accounts);
  // A Super Admin can do everything an Admin can, plus manage users. The
  // backend's admin check already accepts SuperAdmin.
  const realIsSuperAdmin = hasRole(accounts, "SuperAdmin");
  const realIsAdmin = hasRole(accounts, "Admin") || realIsSuperAdmin;
  const realIsTech  = hasRole(accounts, "Tech");
  // Interpreting physician. Narrower than a tech: their own cases, their own
  // half of the QA checks, their own report, and nothing else.
  const realIsIP    = hasRole(accounts, "IP");

  // While an admin is viewing as a tech, report the tech's role to everyone
  // who asks. Swapping here rather than at each call site is what makes this a
  // real tech view - sidebar, route guards and per-page controls all read the
  // same effective role - instead of an admin view with a filtered grid.
  // Anything that must key off who the user actually is (the picker itself,
  // the "stop viewing" control) reads realIsAdmin.
  // Same swap as the tech view: report the impersonated role to everyone who
  // asks, so route guards, the sidebar and per-page controls agree.
  if (viewingAsIP) {
    return {
      roles: ["IP"], isAdmin: false, isTech: false, isSuperAdmin: false, isIP: true,
      realRoles, realIsAdmin, realIsTech, realIsSuperAdmin, realIsIP,
      viewingAsTech: false, viewingAsIP: true,
    };
  }

  if (viewingAsTech) {
    return {
      roles: ["Tech"], isAdmin: false, isTech: true, isSuperAdmin: false, isIP: false,
      realRoles, realIsAdmin, realIsTech, realIsSuperAdmin, realIsIP,
      viewingAsTech: true, viewingAsIP: false,
    };
  }
  return {
    // A Super Admin also passes every Admin route guard.
    roles: realIsSuperAdmin && !realRoles.some(r => String(r).toLowerCase() === "admin") ? [...realRoles, "Admin"] : realRoles,
    isAdmin: realIsAdmin, isTech: realIsTech, isSuperAdmin: realIsSuperAdmin,
    // An admin who also holds IP is an admin: the narrower role would hide
    // their own admin pages from them.
    isIP: realIsIP && !realIsAdmin,
    realRoles, realIsAdmin, realIsTech, realIsSuperAdmin, realIsIP,
    viewingAsTech: false, viewingAsIP: false,
  };
}
